import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFieldValue,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScriptsv2";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  FormInput,
  FormRadioButton,
  FormTextArea,
} from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getFormsySubmissionsByFormIdForClient } from "../services/soar";
import { getSchema } from "./joi/validation";

function SCPPSelfExit() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const [loadStatus, setLoadStatus] = useState({});
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (initialProgram?.ccx_client_programid && loadStatus.dataSet) {
      setData((d) => {
        const obj = { ...d };

        if (
          obj.SelectedProgram === undefined ||
          getFieldValue(obj.SelectedProgram)
        ) {
          return obj;
        }

        obj.SelectedProgram.value = initialProgram.ccx_client_programid;
        return obj;
      });

      setFormsyDetails((fd) => {
        const details = { ...fd };

        if (details.ClientProgramId === initialProgram.ccx_client_programid) {
          return details;
        }

        details.ClientProgramId = initialProgram.ccx_client_programid;
        details.ClientProgramName = getFieldName(
          initialProgram.ccx_programunit
        );
        return details;
      });
    }
  }, [initialProgram, loadStatus.dataSet]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }

        setLoadStatus((ls) => {
          return { ...ls, dataSet: true };
        });
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      !safyInfo?.user ||
      loadStatus.configLoaded ||
      loadStatus.loadingConfig
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadStatus((ls) => {
        return { ...ls, loadingConfig: true };
      });
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadStatus((ls) => {
          return {
            ...ls,
            loadingConfig: false,
            configLoaded: true,
            dataSet: true,
          };
        });
      } else {
        setLoadStatus((ls) => {
          return { ...ls, loadingConfig: false, configLoaded: true };
        });
      }
    };

    loadConfig();
  }, [
    formid,
    clientid,
    safyInfo,
    submissionid,
    loadStatus.configLoaded,
    loadStatus.loadingConfig,
  ]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.value) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property].type !== "section" &&
        data[property].type !== "label" &&
        data[property].type !== "User" &&
        data[property].type !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property].label !== undefined && data[property].label !== ""
              ? data[property].label
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [selfEntryAssessments, setSelfEntryAssessments] = useState([]);

  useEffect(() => {
    const controller = new AbortController();

    const getSelfEntryAssessments = async () => {
      const controllerSignal = controller.signal;
      const response = await getFormsySubmissionsByFormIdForClient(
        "AB8E59C8-51DE-EF11-88F9-FC68272BBF38", // SC-PP-SelfEntry Form Container Id
        clientid,
        controllerSignal
      );

      if (!controllerSignal.aborted && response) {
        const assessments = JSON.parse(response);

        if (assessments?.FormSubmissions?.Submission?.length > 0) {
          const activeAssessments =
            assessments.FormSubmissions.Submission.filter(
              (ir) => ir.Active === "1" && ir.FinalSigned === "1"
            ).sort((a, b) => {
              let aDate = moment(getFieldValue(a.FormResults.results.Date));
              let bDate = moment(getFieldValue(b.FormResults.results.Date));
              const difference = bDate.diff(aDate);

              if (difference === 0) {
                aDate = moment(a.CreatedOn);
                bDate = moment(b.CreatedOn);
                return bDate.diff(aDate);
              } else {
                return difference;
              }
            });
          setSelfEntryAssessments(activeAssessments);
        }
      }
    };

    getSelfEntryAssessments();

    return () => controller.abort();
  }, [clientid]);

  useEffect(() => {
    if (
      !loadStatus.dataSet ||
      !data.SelectedProgram?.value ||
      selfEntryAssessments.length === 0
    ) {
      return;
    }

    const programAssessments = selfEntryAssessments.filter(
      (a) => `{${a.ClientProgramId}}` === data.SelectedProgram.value
    );

    if (programAssessments.length === 0) {
      return;
    }

    const mostRecent = programAssessments.sort((a, b) => {
      const aDate = moment(getFieldValue(a.FormResults.results.Date));
      const bDate = moment(getFieldValue(b.FormResults.results.Date));
      return aDate.diff(bDate);
    })[0];

    setData((d) => {
      const obj = { ...d };
      obj.FindResourcesEntry.value = getFieldValue(
        mostRecent.FormResults.results.FindResources
      );
      obj.GetResourcesEntry.value = getFieldValue(
        mostRecent.FormResults.results.GetResources
      );
      obj.SafeEntry.value = getFieldValue(mostRecent.FormResults.results.Safe);
      obj.DecisionsEntry.value = getFieldValue(
        mostRecent.FormResults.results.Decisions
      );
      obj.ListenEntry.value = getFieldValue(
        mostRecent.FormResults.results.Listen
      );
      obj.SupportEntry.value = getFieldValue(
        mostRecent.FormResults.results.Support
      );
      obj.CommunicationEntry.value = getFieldValue(
        mostRecent.FormResults.results.Communication
      );
      obj.CrisisEntry.value = getFieldValue(
        mostRecent.FormResults.results.Crisis
      );
      obj.AdvocateEntry.value = getFieldValue(
        mostRecent.FormResults.results.Advocate
      );
      obj.UnderstandEntry.value = getFieldValue(
        mostRecent.FormResults.results.Understand
      );
      obj.ComfortableEntry.value = getFieldValue(
        mostRecent.FormResults.results.Comfortable
      );
      return obj;
    });
  }, [loadStatus.dataSet, data.SelectedProgram?.value, selfEntryAssessments]);

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <p>
                This form is to be completed by the Parent Partner with the
                participant upon exit from the Parent Partner Program.
                <ul>
                  <li>
                    First, ask the participant to "think back to when they first
                    began the Parent Partner Program" and assess their level
                    when they first started. The RETROSPECTIVE ASSESSMENT column
                    represents the participant's self-assessment completed at
                    Intake.
                  </li>
                  <li>
                    Then ask the participant to assess their current level. Mark
                    the number that corresponds to the participant's EXIT
                    self-assessment for each scale item in the right column.
                  </li>
                </ul>
              </p>
              <FormInput
                name="Date"
                label="Date:"
                error={errors.Date}
                value={getFieldValue(data.Date)}
                onChange={handleChange}
                identical={compare("Date")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                type="date"
              />
              <div className="container border">
                <div className="row border-bottom">
                  <div className="col py-2 text-center">
                    Using the scale below, mark the number in the correct column
                    to indicate the participant's assessment on each item.
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2 text-center border-end">
                    Never (1)
                  </div>
                  <div className="col py-2 text-center border-end">
                    Rarely (2)
                  </div>
                  <div className="col py-2 text-center border-end">
                    Sometimes (3)
                  </div>
                  <div className="col py-2 text-center border-end">
                    Often (4)
                  </div>
                  <div className="col py-2 text-center">Always (5)</div>
                </div>
                <div className="row border-bottom ">
                  <div className="col py-2 ">
                    <b>
                      RETROSPECTIVE
                      <br />
                      ASSESSMENT
                    </b>
                  </div>
                  <div className="col"></div>
                  <div className="col py-2 text-end">
                    <b>
                      EXIT
                      <br />
                      ASSESSMENT
                    </b>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    {getFieldValue(data.FindResourcesEntry)}
                  </div>
                  <div className="col-1 py-3 border-end">
                    <span className="float-end">1.</span>
                  </div>
                  <div className="col py-3 border-end">
                    I am able to find the community resources I need to keep my
                    child(ren) safe.
                  </div>
                  <div className="col-1 py-2">
                    <FormInput
                      name="FindResourcesExit"
                      value={getFieldValue(data.FindResourcesExit)}
                      onChange={handleChange}
                      identical={compare("FindResourcesExit")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      style={{ marginBottom: "0px" }}
                    />
                  </div>
                </div>
                {errors.FindResourcesExit !== undefined ? (
                  <div className="row border-bottom">
                    <div className="col pt-3 clearfix">
                      {errors.FindResourcesExit !== undefined ? (
                        <div className="alert alert-danger float-end">
                          {errors.FindResourcesExit}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    {getFieldValue(data.GetResourcesEntry)}
                  </div>
                  <div className="col-1 py-3 border-end">
                    <span className="float-end">2.</span>
                  </div>
                  <div className="col py-3 border-end">
                    I am able to complete the steps necessary to get the
                    community resources I need.
                  </div>
                  <div className="col-1 py-2">
                    <FormInput
                      name="GetResourcesExit"
                      value={getFieldValue(data.GetResourcesExit)}
                      onChange={handleChange}
                      identical={compare("GetResourcesExit")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      style={{ marginBottom: "0px" }}
                    />
                  </div>
                </div>
                {errors.GetResourcesExit !== undefined ? (
                  <div className="row border-bottom">
                    <div className="col pt-3 clearfix">
                      {errors.GetResourcesExit !== undefined ? (
                        <div className="alert alert-danger float-end">
                          {errors.GetResourcesExit}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    {getFieldValue(data.SafeEntry)}
                  </div>
                  <div className="col-1 py-3 border-end">
                    <span className="float-end">3.</span>
                  </div>
                  <div className="col py-3 border-end">
                    I am able to effectively manage my situation to keep my
                    child(ren) safe when times are stressful.
                  </div>
                  <div className="col-1 py-2">
                    <FormInput
                      name="SafeExit"
                      value={getFieldValue(data.SafeExit)}
                      onChange={handleChange}
                      identical={compare("SafeExit")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      style={{ marginBottom: "0px" }}
                    />
                  </div>
                </div>
                {errors.SafeExit !== undefined ? (
                  <div className="row border-bottom">
                    <div className="col pt-3 clearfix">
                      {errors.SafeExit !== undefined ? (
                        <div className="alert alert-danger float-end">
                          {errors.SafeExit}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    {getFieldValue(data.DecisionsEntry)}
                  </div>
                  <div className="col-1 py-3 border-end">
                    <span className="float-end">4.</span>
                  </div>
                  <div className="col py-3 border-end">
                    I am able to make the appropriate decisions for myself and
                    my family.
                  </div>
                  <div className="col-1 py-2">
                    <FormInput
                      name="DecisionsExit"
                      value={getFieldValue(data.DecisionsExit)}
                      onChange={handleChange}
                      identical={compare("DecisionsExit")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      style={{ marginBottom: "0px" }}
                    />
                  </div>
                </div>
                {errors.DecisionsExit !== undefined ? (
                  <div className="row border-bottom">
                    <div className="col pt-3 clearfix">
                      {errors.DecisionsExit !== undefined ? (
                        <div className="alert alert-danger float-end">
                          {errors.DecisionsExit}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    {getFieldValue(data.ListenEntry)}
                  </div>
                  <div className="col-1 py-3 border-end">
                    <span className="float-end">5.</span>
                  </div>
                  <div className="col py-3 border-end">
                    I have others who will listen when I need to talk about my
                    problems.
                  </div>
                  <div className="col-1 py-2">
                    <FormInput
                      name="ListenExit"
                      value={getFieldValue(data.ListenExit)}
                      onChange={handleChange}
                      identical={compare("ListenExit")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      style={{ marginBottom: "0px" }}
                    />
                  </div>
                </div>
                {errors.ListenExit !== undefined ? (
                  <div className="row border-bottom">
                    <div className="col pt-3 clearfix">
                      {errors.ListenExit !== undefined ? (
                        <div className="alert alert-danger float-end">
                          {errors.ListenExit}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    {getFieldValue(data.SupportEntry)}
                  </div>
                  <div className="col-1 py-3 border-end">
                    <span className="float-end">6.</span>
                  </div>
                  <div className="col py-3 border-end">
                    I have others who will support positive choices and changes
                    I make.
                  </div>
                  <div className="col-1 py-2">
                    <FormInput
                      name="SupportExit"
                      value={getFieldValue(data.SupportExit)}
                      onChange={handleChange}
                      identical={compare("SupportExit")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      style={{ marginBottom: "0px" }}
                    />
                  </div>
                </div>
                {errors.SupportExit !== undefined ? (
                  <div className="row border-bottom">
                    <div className="col pt-3 clearfix">
                      {errors.SupportExit !== undefined ? (
                        <div className="alert alert-danger float-end">
                          {errors.SupportExit}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    {getFieldValue(data.CommunicationEntry)}
                  </div>
                  <div className="col-1 py-3 border-end">
                    <span className="float-end">7.</span>
                  </div>
                  <div className="col py-3 border-end">
                    I talk reasonably and honestly with others about my
                    situation and problems.
                  </div>
                  <div className="col-1 py-2">
                    <FormInput
                      name="CommunicationExit"
                      value={getFieldValue(data.CommunicationExit)}
                      onChange={handleChange}
                      identical={compare("CommunicationExit")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      style={{ marginBottom: "0px" }}
                    />
                  </div>
                </div>
                {errors.CommunicationExit !== undefined ? (
                  <div className="row border-bottom">
                    <div className="col pt-3 clearfix">
                      {errors.CommunicationExit !== undefined ? (
                        <div className="alert alert-danger float-end">
                          {errors.CommunicationExit}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    {getFieldValue(data.CrisisEntry)}
                  </div>
                  <div className="col-1 py-3 border-end">
                    <span className="float-end">8.</span>
                  </div>
                  <div className="col py-3 border-end">
                    If there is a crisis in my life I have someone I can talk
                    to.
                  </div>
                  <div className="col-1 py-2">
                    <FormInput
                      name="CrisisExit"
                      value={getFieldValue(data.CrisisExit)}
                      onChange={handleChange}
                      identical={compare("CrisisExit")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      style={{ marginBottom: "0px" }}
                    />
                  </div>
                </div>
                {errors.CrisisExit !== undefined ? (
                  <div className="row border-bottom">
                    <div className="col pt-3 clearfix">
                      {errors.CrisisExit !== undefined ? (
                        <div className="alert alert-danger float-end">
                          {errors.CrisisExit}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    {getFieldValue(data.AdvocateEntry)}
                  </div>
                  <div className="col-1 py-3 border-end">
                    <span className="float-end">9.</span>
                  </div>
                  <div className="col py-3 border-end">
                    I am able to effectively speak up for myself and my family
                    to DHS and other service providers.
                  </div>
                  <div className="col-1 py-2">
                    <FormInput
                      name="AdvocateExit"
                      value={getFieldValue(data.AdvocateExit)}
                      onChange={handleChange}
                      identical={compare("AdvocateExit")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      style={{ marginBottom: "0px" }}
                    />
                  </div>
                </div>
                {errors.AdvocateExit !== undefined ? (
                  <div className="row border-bottom">
                    <div className="col pt-3 clearfix">
                      {errors.AdvocateExit !== undefined ? (
                        <div className="alert alert-danger float-end">
                          {errors.AdvocateExit}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    {getFieldValue(data.UnderstandEntry)}
                  </div>
                  <div className="col-1 py-3 border-end">
                    <span className="float-end">10.</span>
                  </div>
                  <div className="col py-3 border-end">
                    I am able to listen to DHS and other service providers and
                    understand their concerns with my situation.
                  </div>
                  <div className="col-1 py-2">
                    <FormInput
                      name="UnderstandExit"
                      value={getFieldValue(data.UnderstandExit)}
                      onChange={handleChange}
                      identical={compare("UnderstandExit")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      style={{ marginBottom: "0px" }}
                    />
                  </div>
                </div>
                {errors.UnderstandExit !== undefined ? (
                  <div className="row border-bottom">
                    <div className="col pt-3 clearfix">
                      {errors.UnderstandExit !== undefined ? (
                        <div className="alert alert-danger float-end">
                          {errors.UnderstandExit}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <div className="row border-bottom">
                  <div className="col-1 py-2 border-end">
                    {getFieldValue(data.ComfortableEntry)}
                  </div>
                  <div className="col-1 py-3 border-end">
                    <span className="float-end">11.</span>
                  </div>
                  <div className="col py-3 border-end">
                    I feel comfortable when talking with my DHS worker or other
                    service providers.
                  </div>
                  <div className="col-1 py-2">
                    <FormInput
                      name="ComfortableExit"
                      value={getFieldValue(data.ComfortableExit)}
                      onChange={handleChange}
                      identical={compare("ComfortableExit")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      style={{ marginBottom: "0px" }}
                    />
                  </div>
                </div>
                {errors.ComfortableExit !== undefined ? (
                  <div className="row border-bottom">
                    <div className="col pt-3 clearfix">
                      {errors.ComfortableExit !== undefined ? (
                        <div className="alert alert-danger float-end">
                          {errors.ComfortableExit}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <div className="row border-bottom">
                  <div className="col py-2">
                    <br />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    <b>
                      Using the scale below, please mark the box that best
                      describes your current relationship with your DHS worker.
                    </b>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div
                    className="col pt-3 text-center border-end"
                    onClick={() => {
                      const e = {
                        currentTarget: {
                          name: "Relationship",
                          value: "Very Negative",
                        },
                      };
                      handleChange(e);
                    }}
                  >
                    <FormRadioButton
                      name="Relationship"
                      value={getFieldValue(data.Relationship)}
                      options={[
                        { _id: "Very Negative", name: "Very Negative" },
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col pt-3 text-center border-end"
                    onClick={() => {
                      const e = {
                        currentTarget: {
                          name: "Relationship",
                          value: "Negative",
                        },
                      };
                      handleChange(e);
                    }}
                  >
                    <FormRadioButton
                      name="Relationship"
                      value={getFieldValue(data.Relationship)}
                      options={[{ _id: "Negative", name: "Negative" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col pt-3 text-center border-end"
                    onClick={() => {
                      const e = {
                        currentTarget: {
                          name: "Relationship",
                          value: "Neutral",
                        },
                      };
                      handleChange(e);
                    }}
                  >
                    <FormRadioButton
                      name="Relationship"
                      value={getFieldValue(data.Relationship)}
                      options={[{ _id: "Neutral", name: "Neutral" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col pt-3 text-center border-end"
                    onClick={() => {
                      const e = {
                        currentTarget: {
                          name: "Relationship",
                          value: "Positive",
                        },
                      };
                      handleChange(e);
                    }}
                  >
                    <FormRadioButton
                      name="Relationship"
                      value={getFieldValue(data.Relationship)}
                      options={[{ _id: "Positive", name: "Positive" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col pt-3 text-center"
                    onClick={() => {
                      const e = {
                        currentTarget: {
                          name: "Relationship",
                          value: "Very Positive",
                        },
                      };
                      handleChange(e);
                    }}
                  >
                    <FormRadioButton
                      name="Relationship"
                      value={getFieldValue(data.Relationship)}
                      options={[
                        { _id: "Very Positive", name: "Very Positive" },
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.Relationship !== undefined ? (
                  <div className="row border-bottom">
                    <div className="col pt-3 clearfix">
                      <div className="alert alert-danger">
                        {errors.Relationship}
                      </div>
                    </div>
                  </div>
                ) : null}
                <div className="row border-bottom">
                  <div className="col py-2">
                    <br />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    Please use the space below to provide any additional
                    comments regarding any of the statements above.
                  </div>
                </div>
                <div className="row">
                  <div className="col py-2">
                    <FormTextArea
                      name="Comments"
                      error={errors.Comments}
                      value={getFieldValue(data.Comments)}
                      onChange={handleChange}
                      identical={compare("Comments")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      rows="5"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default SCPPSelfExit;
